import { ChevronDown, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

export interface MultiSelectFilterOption {
  value: string;
  label: string;
}

export interface MultiSelectFilterProps {
  label: string;
  options: MultiSelectFilterOption[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Compact mode hides the uppercase label (for inset toolbars); the trigger
   *  carries the name via aria-label instead, matching SelectFilter. */
  compact?: boolean;
  /** When true, the trigger stretches to fill its container instead of using
   *  the fixed 180px minimum. */
  fluid?: boolean;
}

export function MultiSelectFilter({
  label,
  options,
  value,
  onChange,
  placeholder,
  compact = false,
  fluid = false,
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const selectedSet = new Set(value);
  const hasSelection = value.length > 0;
  const triggerLabel = hasSelection ? `${label}: ${String(value.length)}` : (placeholder ?? label);

  return (
    <div className={cn('flex min-w-0 flex-col gap-1', fluid && 'w-full')}>
      {compact ? null : (
        <span className="text-meta-sm tracking-label font-medium text-neutral-500 uppercase">
          {label}
        </span>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={label}
            className={cn(
              'bg-surface inline-flex h-10 items-center justify-between gap-2 rounded-md border border-(--color-input) px-3',
              'text-body-md text-neutral-900',
              fluid ? 'w-full' : 'min-w-45',
              'transition-colors duration-150 hover:border-neutral-300',
              'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none',
            )}
          >
            <span className={cn('truncate', !hasSelection && 'text-neutral-400')}>
              {triggerLabel}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-neutral-500" strokeWidth={1.5} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={4} className="w-72 p-0">
          <div className="flex items-center gap-2 border-b border-(--color-border) px-3 py-2">
            <Search className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              placeholder="Search"
              className="text-body-md w-full bg-transparent text-neutral-900 placeholder:text-neutral-400 focus:outline-none"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="text-meta-sm px-3 py-6 text-center text-neutral-500">No matches.</div>
            ) : (
              filtered.map((opt) => {
                const checked = selectedSet.has(opt.value);
                return (
                  <label
                    key={opt.value}
                    className={cn(
                      'text-body-md flex cursor-pointer items-center gap-3 px-3 py-2 text-neutral-900',
                      'transition-colors hover:bg-neutral-100',
                    )}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => {
                        if (v) onChange([...value, opt.value]);
                        else onChange(value.filter((x) => x !== opt.value));
                      }}
                    />
                    {opt.label}
                  </label>
                );
              })
            )}
          </div>
          {hasSelection ? (
            <>
              <Separator />
              <div className="flex justify-between p-2">
                <button
                  type="button"
                  onClick={() => {
                    onChange(options.map((o) => o.value));
                  }}
                  className="text-meta-sm rounded-md px-2 py-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onChange([]);
                  }}
                  className="text-meta-sm rounded-md px-2 py-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
                >
                  Clear
                </button>
              </div>
            </>
          ) : null}
        </PopoverContent>
      </Popover>
    </div>
  );
}
